import { ActionLogMetadata, CreateActionLogData, TimelineStatus } from "@/shared/types/actionLog";

// Helper functions for creating different types of action logs

export const actionLogTemplates = {
  // Timeline status updates (for confirmation screen)
  createStatusUpdate: (
    estateId: number,
    title: string,
    description: string,
    date: string = new Date().toISOString().split("T")[0],
    status: TimelineStatus = "completed",
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "status_update",
    action_category: "timeline",
    title,
    description,
    timeline_date: date,
    timeline_status: status,
    metadata,
    is_visible_to_user: true,
  }),

  // Task completion (for Recent Updates card)
  createTaskCompleted: (
    estateId: number,
    taskName: string,
    completedBy: "user" | "elayne",
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "task_completed",
    action_category: "system",
    title: `${taskName}`,
    description: completedBy === "elayne" ? "Elayne completed" : "You completed",
    metadata: {
      ...metadata,
      completed_by: completedBy,
    },
    is_visible_to_user: true,
  }),

  // Task started (for Recent Updates card)
  createTaskStarted: (
    estateId: number,
    taskName: string,
    startedBy: "user" | "elayne",
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "task_started",
    action_category: "system",
    title: `${taskName}`,
    description: startedBy === "elayne" ? "Elayne is processing" : "In Progress",
    metadata: {
      ...metadata,
      started_by: startedBy,
    },
    is_visible_to_user: true,
  }),

  // Document upload
  createDocumentUpload: (
    estateId: number,
    fileName: string,
    documentType: string,
    documentId: number,
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "document_upload",
    action_category: "user_action",
    title: "Document uploaded",
    description: `Uploaded ${fileName}`,
    metadata: {
      ...metadata,
      document_id: documentId,
      file_name: fileName,
      document_type: documentType,
    },
    is_visible_to_user: true,
  }),

  // Document delete
  createDocumentDelete: (
    estateId: number,
    fileName: string,
    documentType: string,
    documentId: number,
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "document_delete",
    action_category: "user_action",
    title: "Document removed",
    description: `Removed ${fileName}`,
    metadata: {
      ...metadata,
      document_id: documentId,
      file_name: fileName,
      document_type: documentType,
    },
    is_visible_to_user: true,
  }),

  // Admin note
  createAdminNote: (
    estateId: number,
    title: string,
    description: string,
    adminId: number,
    isVisibleToUser: boolean = false,
    metadata: ActionLogMetadata = {},
  ): CreateActionLogData => ({
    estate_id: estateId,
    action_type: "admin_note",
    action_category: "admin_action",
    title,
    description,
    created_by_admin: adminId,
    is_visible_to_user: isVisibleToUser,
    metadata,
  }),
};

// Default timeline steps for different task types
export const defaultTimelineSteps = {
  "social-security": [
    {
      title: "Submitted to SSA",
      description: "Initial notification submitted to the Social Security Administration",
      status: "completed" as TimelineStatus,
    },
    {
      title: "Processing at SSA",
      description: "SSA is processing the notification and updating their records",
      status: "in_progress" as TimelineStatus,
    },
    {
      title: "Benefits review",
      description: "Reviewing survivor benefits eligibility and payment adjustments",
      status: "pending" as TimelineStatus,
    },
  ],
  "bank-account": [
    {
      title: "Submitted to Bank",
      description: "Account closure request submitted to the financial institution",
      status: "completed" as TimelineStatus,
    },
    {
      title: "Bank processing",
      description: "Bank is processing the account closure and preparing final statements",
      status: "in_progress" as TimelineStatus,
    },
    {
      title: "Account closure",
      description: "Final account closure and distribution of remaining funds",
      status: "pending" as TimelineStatus,
    },
  ],
  "death-certificate": [
    {
      title: "Order submitted",
      description: "Death certificate order submitted to vital records office",
      status: "completed" as TimelineStatus,
    },
    {
      title: "Processing",
      description: "Vital records office is processing the certificate request",
      status: "in_progress" as TimelineStatus,
    },
    {
      title: "Certificate issued",
      description: "Death certificate has been issued and is being mailed",
      status: "pending" as TimelineStatus,
    },
  ],
  default: [
    {
      title: "Submitted",
      description: "Your request has been submitted and is being processed",
      status: "completed" as TimelineStatus,
    },
    {
      title: "Processing",
      description: "Your request is currently being reviewed and processed",
      status: "in_progress" as TimelineStatus,
    },
    {
      title: "Completion",
      description: "Final steps are being completed",
      status: "pending" as TimelineStatus,
    },
  ],
};

export function getTaskTypeFromName(taskName: string): keyof typeof defaultTimelineSteps {
  const name = taskName.toLowerCase();

  if (name.includes("social security") || name.includes("ssa")) {
    return "social-security";
  } else if (name.includes("bank") || name.includes("account")) {
    return "bank-account";
  } else if (name.includes("death certificate")) {
    return "death-certificate";
  }

  return "default";
}

export function createDefaultTimelineSteps(
  estateId: number,
  taskName: string,
  submissionDate: string = new Date().toISOString().split("T")[0],
): CreateActionLogData[] {
  const taskType = getTaskTypeFromName(taskName);
  const templates = defaultTimelineSteps[taskType];
  const baseDate = new Date(submissionDate);

  return templates.map((template, index) =>
    actionLogTemplates.createStatusUpdate(
      estateId,
      template.title,
      template.description,
      index === 0
        ? submissionDate
        : index === 1
          ? new Date(baseDate.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
          : "TBD",
      template.status,
    ),
  );
}
